import {
  Action,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import { BasketStateModel } from './model/basket-state.model';
import { AddItemToBasketAction } from './action/add-item-to-basket.action';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { RemoveItemFromBasketAction } from './action/remove-item-from-basket.action';
import { ClearBasketAction } from './action/clear-basket.action';
import { deepCopy, isNil, isNumeric } from '@citydeals/common';
import { BasketOfferModel } from './model/basket-offer.model';
import { BasketPriceModel } from './model/basket-price.model';
import { BasketCouponModel, CouponType, DiscountTypeEnum } from './model/basket-coupon.model';
import { SaveBasketOverviewFormAction } from './action/save-basket-overview-form.action';
import { BasketOverviewFormModel } from './model/basket-overview-form.model';
import { InitBasketAction } from './action/init-basket.action';
import { ValidateBasketCouponAction } from './action/validate-basket-coupon.action';
import { HttpClient } from '@angular/common/http';
import {
  AddToCartEvent,
  AddToCartTiktokEvent,
  CheckoutEvent,
  RemoveFromCartEvent,
  TrackService,
  TiktokTrackService,
} from '@citydeals/track';
import { RemoveCouponAction } from './action/remove-coupon.action';
import { environment } from '@citydeals/env';
import {catchError, filter, tap, throwError} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  AuthState,
  LoginAction,
  LoginWithTokenAction,
  LogoutAction,
} from '@citydeals/auth';
import { LoadBasketRemoteStateAction } from './action/load-basket-remote-state.action';
import { BasketAddressFormModel } from './model/basket-address-form.model';
import { SaveBasketAddressFormAction } from './action/save-basket-address-form.action';
import { MatSnackBar } from '@angular/material/snack-bar';
import {RemoveGiftAction} from "./action/remove-gift.actions";
import { SaveBasketGiftPackageAction } from './action/save-basket-gift-package.action';
import { GiftWrapperBoxModel } from './model/basket-overview-gift-form.model';
import { SaveBasketGiftPackageShippingPriceAction } from './action/save-basket-gift-package-shipping-price.action';

const defaultState: BasketStateModel = {
  offers: [],
  coupon: null,
  shipping: null,
  addressForm: null,
  decorativePackaging: [],
  overviewForm: { gifts: {}, couponCode: '' },
};
export const basketStateName = 'citydeals_basket';
@State<BasketStateModel>({
  name: basketStateName,
  defaults: defaultState,
})
@Injectable()
export class BasketState implements NgxsOnInit {
  private afterLogin = false;
  @Selector()
  static offersCount(state: BasketStateModel): number {
    return state.offers?.length ?? 0;
  }

  @Selector()
  static offers(state: BasketStateModel): BasketOfferModel[] {
    return state.offers;
  }

  @Selector()
  static price(state: BasketStateModel): BasketPriceModel {
    let total = 0;
    let discount = 0;
    let couponDiscount = 0;
    let giftPackagesTotal = 0;
    let shippingPrice = 0;

    for (const offer of state.offers) {
      total += offer.finalPrice;

      if (!isNil(state.coupon) && state.coupon.coupon_type === CouponType.PACKAGE &&
        state.coupon.packages.some(pkg => pkg.id === offer.id)) {
        couponDiscount += offer.finalPrice * offer.quantity * (state.coupon.discount / 100);
      }

      discount += offer.originalPrice * offer.quantity * (offer.discountValue / 100);
    }

    shippingPrice = state.shipping?.price || 0;

    if (!isNil(state.decorativePackaging) && state.decorativePackaging.length > 0) {
      giftPackagesTotal = state.decorativePackaging.reduce(
        (acc, box) => acc + (box.gross_price || 0), 0
      );
    }

    if (!isNil(state.coupon) && state.coupon.coupon_type === CouponType.GLOBAL) {
      if (state.coupon.discount_type === DiscountTypeEnum.PERCENT) {
        couponDiscount = Math.round((total / 100) * state.coupon.discount);
      }
      else if (state.coupon.discount_type === DiscountTypeEnum.FIX) {
        couponDiscount = state.coupon.discount;
      }
    }

    // Ajándékcsomagok hozzáadása
    total += (giftPackagesTotal + shippingPrice);
    // Kupon kedvezmény alkalmazása
    total -= couponDiscount;
    // Kupon kedvezmény alkalmazása
    discount += couponDiscount;
    return { total, discount, giftPackagesTotal, shippingPrice };
  }

  @Selector()
  static coupon(state: BasketStateModel): BasketCouponModel | null {
    return state.coupon;
  }

  @Selector()
  static overviewForm(state: BasketStateModel): BasketOverviewFormModel {
    return state.overviewForm;
  }

  @Selector()
  static decorativePackaging(state: BasketStateModel): GiftWrapperBoxModel[] | null {
    return state.decorativePackaging;
  }

  @Selector()
  static addressForm(state: BasketStateModel): BasketAddressFormModel | null {
    return state.addressForm;
  }

  constructor(
    private trackService: TrackService,
    private tiktokTrackService: TiktokTrackService,
    private http: HttpClient,
    private store: Store,
    private matSnackBar: MatSnackBar,
  ) {}

  ngxsOnInit(ctx?: StateContext<any>) {
    this.store
      .select(AuthState.isLoggedIn)
      .pipe(filter((isLoggedIn) => isLoggedIn))
      .subscribe(() => this.store.dispatch(new LoadBasketRemoteStateAction()));
  }

  @Action([LoginAction, LoginWithTokenAction])
  loginAction() {
    this.afterLogin = true;
  }

  @Action(LoadBasketRemoteStateAction)
  @ImmutableContext()
  loadBasketRemoteState({
    setState,
    getState,
    dispatch,
  }: StateContext<BasketStateModel>) {
    const prevState = deepCopy(getState());
    return this.http
      .get<{ data: BasketOfferModel[] }>(
        `${environment.backendUrl}/client/cart`
      )
      .pipe(
        tap((response: any) => {
          setState((state: BasketStateModel) => {
            state.offers = response.data;
            return state;
          });
        }),
        tap(() => {
          // ha belepes elott volt mar valami a kosarban
          if (this.afterLogin && prevState.offers.length > 0) {
            this.afterLogin = false;
            dispatch(
              prevState.offers.map(
                (offer) =>
                  new AddItemToBasketAction(
                    offer,
                    offer.quantity
                  )
/*
                  new AddItemToBasketAction(
                    { id: offer.id } as BasketOfferModel,
                    offer.quantity
                  )
*/
              )
            );
          }
        })
      );
  }

  @Action(AddItemToBasketAction)
  @ImmutableContext()
  addItemToBasket(
    { setState, getState }: StateContext<BasketStateModel>,
    { product, count }: AddItemToBasketAction
  ) {
    if (this.store.selectSnapshot(AuthState.isLoggedIn)) {
      const currentState = getState();
      let existProductId = -1;
      if (!isNil(product.idx) && isNumeric(product.idx)) {
        // update
        existProductId = currentState.offers.findIndex(
          (offer) => offer.idx === product.idx
        );
        const offer = currentState.offers[existProductId];
        return this.http
          .post<{ data: BasketOfferModel }>(
            `${environment.backendUrl}/client/cart/quantity/${offer.idx}`,
            { quantity: offer.quantity + count }
          )
          .pipe(
            tap((response) => {
              setState((state: BasketStateModel) => {
                state.offers[existProductId] = response.data;
                return state;
              });
              this.googleEventAddToCart(product);
              this.tiktokEventAddToCart(product);
            }),
            catchError((error) => {
              console.error('Error adding the product to the basket', error);
              const errorMessages = error?.error.errors
                ? Object.values(error.error.errors).reduce((acc: string[], current) => {
                  if (Array.isArray(current)) {
                    return acc.concat(current);
                  }
                  return acc;
                }, [])
                : ['Unknown error occurred.'];

              const displayMessage = errorMessages.join('\n');

              this.matSnackBar.open(displayMessage, undefined, {
                panelClass: ['snackBarStyleForError'],
                duration: 2000,
              });
              return throwError(() => error);
            })
          );
      } else {
        // create
        return this.http
          .post<{ data: BasketOfferModel }>(
            `${environment.backendUrl}/client/cart`,
            { packageId: product.id, quantity: count }
          )
          .pipe(
            tap((response) => {
              setState((state: BasketStateModel) => {
                state.offers.push(response.data);
                return state;
              });
              this.googleEventAddToCart(product);
              this.tiktokEventAddToCart(product);
            })
          );
      }
    }
    else {
      return setState((state: BasketStateModel) => {
        const existProductId = this.findProductById(state, product.idx);
        if (existProductId > -1) {
          const p = state.offers[existProductId];
          state.offers[existProductId] = {
            ...p,
            quantity: p.quantity + count,
            finalPrice: p.finalPrice + p.price * count,
          };
        }
        else {
          product.idx = uuidv4();
          product.finalPrice = product.price * count;
          state.offers.push(product);
        }
        this.googleEventAddToCart(product);
        this.tiktokEventAddToCart(product);
        return state;
      });
    }
  }

  private googleEventAddToCart(product: BasketOfferModel) {
    const event = new AddToCartEvent({
      name: product.voucherTitle,
      id: product.id,
      price: product.finalPrice,
      quantity: 1,
      brand: '',
      category: '',
    });
    this.trackService.track(event);
  }

  private tiktokEventAddToCart(product: BasketOfferModel) {
    const event = new AddToCartTiktokEvent({
      name: product.voucherTitle,
      id: product.id,
      price: product.finalPrice,
      brand: '',
      category: '',
      quantity: 1,
      type: 'product',
      currency: 'HUF',
    });
    this.tiktokTrackService.track(event);
  }

  private googleEventRemoveFromCart(product: BasketOfferModel, count: number) {
    const event = new RemoveFromCartEvent({
      id: product.id,
      quantity: count,
      price: product.finalPrice,
      name: product.voucherTitle,
    });
    this.trackService.track(event);
  }

  @Action(RemoveItemFromBasketAction)
  @ImmutableContext()
  removeItemFromBasket(
    { setState, getState }: StateContext<BasketStateModel>,
    { idx, count }: RemoveItemFromBasketAction
  ) {
    if (this.store.selectSnapshot(AuthState.isLoggedIn)) {
      const currentState = getState();
      const existProductId = this.findProductById(currentState, idx);
      if (existProductId === -1) {
        console.error('Not found basket product: ', idx);
        return;
      }
      else {
        const offer = currentState.offers[existProductId];
        if (offer.quantity - count === 0) {
          return this.http
            .delete(`${environment.backendUrl}/client/cart/${offer.idx}`)
            .pipe(
              tap(() => {
                setState((state: BasketStateModel) => {
                  const basketItem = state.offers[existProductId];
                  if (!isNil(state.overviewForm.gifts[basketItem.idx])) {
                    delete state.overviewForm.gifts[basketItem.idx];
                    if (!state.overviewForm.gifts.gifts || Object.keys(state.overviewForm.gifts).length === 0) {
                      state.shipping = null;
                      state.decorativePackaging = null;
                    }
                  }
                  state.offers.splice(existProductId, 1);
                  return state;
                });
              })
            );
        }
        else {
          return this.http
            .post<{ data: BasketOfferModel }>(
              `${environment.backendUrl}/client/cart/quantity/${offer.idx}`,
              { quantity: offer.quantity - count }
            )
            .pipe(
              tap((response) => {
                setState((state: BasketStateModel) => {
                  state.offers[existProductId] = response.data;
                  return state;
                });
                this.googleEventRemoveFromCart(offer, count);
                // TODO: TIKTOK track RemoveFromCart
              })
            );
        }
      }
    }
    else {
      return setState((state: BasketStateModel) => {
        const existProductId = this.findProductById(state, idx);
        if (existProductId === -1) {
          console.error('Not found basket product: ', idx);
        }
        else {
          const p = state.offers[existProductId];
          state.offers[existProductId] = {
            ...p,
            quantity: p.quantity - count,
            finalPrice: p.finalPrice - p.price * count,
          };
          this.googleEventRemoveFromCart(state.offers[existProductId], count);
          // TODO: TIKTOK track RemoveFromCart
          if (state.offers[existProductId].quantity === 0) {
            if (!isNil(state.overviewForm.gifts[p.idx])) {
              delete state.overviewForm.gifts[p.idx];
              if (!state.overviewForm.gifts.gifts || Object.keys(state.overviewForm.gifts).length === 0) {
                state.shipping = null;
                state.decorativePackaging = null;
              }
            }
            state.offers.splice(existProductId, 1);
          }
        }
        return state;
      });
    }
  }

  @Action(ClearBasketAction)
  @ImmutableContext()
  clearBasket({ setState }: StateContext<BasketStateModel>) {
    setState((state: BasketStateModel) => {
      state.offers = [];
      state.coupon = null;
      state.shipping = null;
      state.decorativePackaging = [];
      state.overviewForm = { gifts: {}, couponCode: '' };
      state.addressForm = null;
      return state;
    });
  }

  @Action(RemoveCouponAction)
  @ImmutableContext()
  removeCoupon({ setState }: StateContext<BasketStateModel>) {
    setState((state: BasketStateModel) => {
      state.coupon = null;
      return state;
    });
  }

  private findProductById(state: BasketStateModel, idx: string): number {
    return state.offers.findIndex((p) => p.idx === idx);
  }

  @Action(SaveBasketGiftPackageAction)
  @ImmutableContext()
  saveBasketGiftPackages(
    { setState }: StateContext<BasketStateModel>,
    { packages }: SaveBasketGiftPackageAction
  ) {
    setState((state: BasketStateModel) => {
      state.decorativePackaging = packages;
      if (!packages || packages.length === 0) {
        state.shipping = null;
      }
      return state;
    });
  }

  @Action(SaveBasketGiftPackageShippingPriceAction)
  @ImmutableContext()
  saveGiftPackagesShippingPrice(
    { setState }: StateContext<BasketStateModel>,
    { shipping }: SaveBasketGiftPackageShippingPriceAction
  ) {
    setState((state: BasketStateModel) => {
      state.shipping = shipping;
      return state;
    });
  }

  @Action(SaveBasketOverviewFormAction)
  @ImmutableContext()
  saveBasketOverviewForm(
    { setState }: StateContext<BasketStateModel>,
    { form }: SaveBasketOverviewFormAction
  ) {
    setState((state: BasketStateModel) => {
      state.overviewForm = form;
      if (!form.gifts || Object.keys(form.gifts).length === 0) {
        state.shipping = null;
        state.decorativePackaging = null;
      }
      return state;
    });
  }

  @Action(SaveBasketAddressFormAction)
  @ImmutableContext()
  saveBasketAddressForm(
    { setState }: StateContext<BasketStateModel>,
    { form }: SaveBasketAddressFormAction
  ) {
    setState((state: BasketStateModel) => {
      state.addressForm = form;
      return state;
    });
  }

  @Action(InitBasketAction)
  @ImmutableContext()
  initBasket(
    { getState }: StateContext<BasketStateModel>,
    { step }: InitBasketAction
  ) {
    const state = getState();
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    const products = state.offers.map((offer) => ({
      name: offer.voucherTitle,
      id: offer.id,
      price: offer.finalPrice,
      quantity: offer.quantity,
    }));
    const event = new CheckoutEvent(currentUser?.userId, step, products);

    this.trackService.track(event);
  }

  @Action(ValidateBasketCouponAction)
  @ImmutableContext()
  validateCoupon(
    { setState }: StateContext<BasketStateModel>,
    { code }: ValidateBasketCouponAction
  ): void {
    this.http
      .get<{
        success: boolean;
        data: BasketCouponModel;
        error: string;
      }>(`${environment.backendUrl}/client/coupon/${code}`)
      .subscribe((res: any) => {
        if (res.success) {
          setState((state: BasketStateModel) => {
            state.coupon = res.data;
            return state;
          });
        } else {
          this.openCouponCodeErrorPopup();
        }
      });
  }

  @Action(LogoutAction)
  @ImmutableContext()
  logout({ setState }: StateContext<BasketStateModel>) {
    setState((state: BasketStateModel) => {
      return deepCopy(defaultState);
    });
  }

  private openCouponCodeErrorPopup() {
    this.matSnackBar.open('Érvénytelen kupon kód', undefined, {
      panelClass: ['snackBarStyleForError'],
      duration: 2000,
    });
  }

  @Action(RemoveGiftAction)
  removeGift(
    { getState, patchState }: StateContext<BasketStateModel>,
    { invalidIdxKeys }: RemoveGiftAction
  ) {
    const state = getState();
    const updatedGifts = { ...state.overviewForm.gifts };
    invalidIdxKeys.forEach((key) => delete updatedGifts[key]);

    if (Object.keys(updatedGifts).length > 0) {
      patchState({
        overviewForm: {
          ...state.overviewForm,
          gifts: updatedGifts,
        },
      });
    } else {
      patchState({
        overviewForm: {
          ...state.overviewForm,
          gifts: {},
        },
      });
    }
  }
}
